// Phase G4-B — real useSkillGraph.
//
// Replaces the G3 mock's 13-skill MOCK_GRAPH. Reads the real
// microSkills.json (41 skills) + computes per-skill mastery from the
// kid's child_attempts. Returns the same SkillNode shape so consumers
// (SkillTree, useMasteryStats) are unchanged.
//
// Mastery is computed via the Phase C/D engine for skills that have
// attempts; skills with no attempts get mastery: null. The
// MasteryRecord.mastery_score is mapped from the engine's mastery
// bands: durableMastery → 1.0, trueMastery → 0.9, provisionalMastery
// → 0.7, readinessOnly → 0.4, else accuracy-derived. This is a
// display projection of the engine's richer state, not a separate
// mastery formula.

import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { MasteryRecord, MicroSkill } from "@/types/engine";
import type { ChildAttempt } from "@/adaptation/types";
import microSkills from "@/curriculum/data/manifests/microSkills.json" with { type: "json" };
import {
  fetchTierHistoryForChildOrThrow,
  fetchTransferAttemptsForSkillOrThrow,
  HISTORY_DISCOVERY_WINDOW,
  tierHistoryReader,
  type TierHistoryRead,
} from "./attemptLogger";
import { loadLocalAttempts, loadLocalTaggedTransferAttempts, mergeAttempts } from "./localAttemptStore";
import { transferServableSkillIds } from "@/adaptation/transferGating";
// MA-623 — masteryScore (the score-per-skill projection) moved out of this
// file into a shared module so the recap and every session screen's
// pre-session count can consume the SAME projection instead of re-deriving
// it — a private function here was the root of the §E3 two-reader
// divergence (docs/build/specs/engine-truth-2026-09-08.md). See
// masteryProjection.ts's header for the full rationale.
import { computeSkillMasteryProjection } from "./masteryProjection";
// MA-297 (live-verify batch-2): the ONE cross-day confirm predicate,
// exported from progression.ts for exactly this consumer — never re-derived
// here (§00b; the two-mastery-readers risk this hook deliberately pairs is
// useSkillGraph's OWN tier with progression's OWN confirm, over the same
// attempt rows — see confirmGateSecuredSetParity.test.mjs's pins).
import { deriveConfirmedSecure } from "./progression";

export type SkillNode = MicroSkill & {
  mastery: MasteryRecord | null;
  /** MA-297: progression.ts's own cross-day confirm (deriveConfirmedSecure)
   *  over this skill's chronological attempts; false when no attempts.
   *  Display/live-verify projection only — serving reads the aggregator's
   *  SkillState.confirmedSecure, never this field. */
  confirmedSecure: boolean;
};

const SKILLS = microSkills as MicroSkill[];

function toMasteryRecord(
  kidId: string,
  skillId: string,
  attempts: ChildAttempt[],
): MasteryRecord {
  const { state, transferEvidence, delayedReviewEvidence, score } =
    computeSkillMasteryProjection(skillId, attempts);
  const correct = attempts.filter((a) => a.correct).length;
  return {
    kidId,
    microSkillId: skillId,
    mastery_score: score,
    recent_accuracy: state.accuracyRate,
    retention_accuracy: delayedReviewEvidence.passed ? 1 : 0,
    transfer_accuracy: transferEvidence.passed ? 1 : 0,
    error_counts: {},
    last_support_level: null,
    overlays: [],
    attempts_count: attempts.length,
    correct_count: correct,
    last_seen_at: attempts[attempts.length - 1]?.timestamp ?? null,
    due_at: delayedReviewEvidence.nextReviewDueAt ?? null,
  };
}

/** MA-303b RED round 2 (S2/S3/S6) — the pure async core of useSkillGraph's
 *  queryFn, extracted BEHAVIOR-PRESERVINGLY (the hook below just calls
 *  this) so the REAL tree-path wiring — not a test's re-implementation of
 *  it — is unit-testable against a stub data client. `client` is optional
 *  and forwarded only to `fetchTransferAttemptsForSkillOrThrow`; every
 *  production call (the hook omits it) is unaffected. */
export async function buildSkillGraphNodes(
  kidId: string | null | undefined,
  localChildId: string | null | undefined,
  client?: Parameters<typeof fetchTransferAttemptsForSkillOrThrow>[3],
  /** MA-1202 RED r1 S-1: the shared tier-history read (the hook passes
   *  tierHistoryReader(queryClient, ...)); omitted, a direct read. */
  readTierHistory?: TierHistoryRead,
): Promise<SkillNode[]> {
  const { bySkill } = await assembleTreeAttemptRows(kidId, localChildId, client, "swallow", readTierHistory);
  return nodesFromGroupedAttempts(bySkill, kidId ?? localChildId ?? "local");
}

/** MA-1202 RED r1 N-3 — the local mirror's per-child cap
 *  (localAttemptStore's CAP; ma1202's test measures the store against it). A
 *  mirror holding fewer rows has never evicted one. */
export const LOCAL_MIRROR_CAP = 1000;

/** The tree's DISCOVERY window per source (cloud newest-N, local mirror
 *  newest-N). MA-1202: no tier is computed from this window any more; a full
 *  cloud window is completed by ONE paged full-history read
 *  (attemptLogger.fetchTierHistoryForChildOrThrow). */
export const TREE_ROW_WINDOW = HISTORY_DISCOVERY_WINDOW;

/** MA-864 (RED r1 M1, D-1017-11) — the tree's ROW SET, extracted verbatim
 *  from buildSkillGraphNodes so the parent "Skills mastered over time" card
 *  folds exactly the rows the tree folds. MA-1202: every row of every skill
 *  (attemptLogger.fetchTierHistoryForChildOrThrow) merged with the child's WHOLE local mirror, plus
 *  (per transfer-registry skill the child has practised) the tagged-transfer
 *  history, with that skill's evidence dropped whole on a tagged-fetch
 *  failure. `cloudErrors` "swallow" (the tree, unchanged contract): a failed
 *  cloud read reads as no cloud rows, as fetchAllAttemptsForChild always did;
 *  "throw" (the card): it rejects, so a failed read never draws a false line.
 *  `cloudRows` is every cloud row read; `localRows` is the WHOLE local
 *  mirror (RED r1 N-3: no window limits either source any more; a caller
 *  checks the mirror against LOCAL_MIRROR_CAP). `readTierHistory` is the
 *  shared read (RED r1 S-1); omitted, a direct read through `client`. */
export async function assembleTreeAttemptRows(
  kidId: string | null | undefined,
  localChildId: string | null | undefined,
  client?: Parameters<typeof fetchTransferAttemptsForSkillOrThrow>[3],
  cloudErrors: "swallow" | "throw" = "swallow",
  readTierHistory?: TierHistoryRead,
): Promise<{ bySkill: Map<string, ChildAttempt[]>; cloudRows: ChildAttempt[]; localRows: ChildAttempt[] }> {
  let cloud: ChildAttempt[] = [];
  if (kidId) {
    try {
      cloud = readTierHistory ? await readTierHistory(kidId) : await fetchTierHistoryForChildOrThrow(kidId, client);
    } catch (error) {
      if (cloudErrors === "throw") throw error;
      console.error("[useSkillGraph] tree cloud read failed — no cloud rows (fetchAllAttemptsForChild's own contract):", error);
      cloud = [];
    }
  }
  // MA-1202: the whole local mirror (itself capped per child), never its
  // newest-N slice across skills.
  const localAll = loadLocalAttempts(localChildId, Number.MAX_SAFE_INTEGER);
  const all = mergeAttempts(cloud, localAll);
      // Group attempts by skillId.
      const bySkill = groupAttemptsBySkill(all);
      // MA-303b RED round 1 (R1), corrected round 2 (S1) — the 500-row
      // window above is ACROSS ALL SKILLS, so a tagged transfer attempt
      // for a registry skill can scroll out of it after ordinary
      // multi-strand practice (measured: ~12 days at the age-6 full
      // dose), silently flipping D2's `required` back to false and the
      // tree chip back to "Mastered" with no pass ever recorded. Merge in
      // the tagged-transfer history (real `is_transfer_task` column
      // filter) for each registered skill the child has actually
      // practised, via the SAME `mergeAttempts` dedup helper used above —
      // the ONE merge helper, reused, not a second implementation. The
      // registry is small (currently one skill) so this is at most one
      // extra query per registered, practised skill.
      // MA-1202: the cloud read above now holds the child's full history,
      // so for a registry skill in the curriculum this
      // merge adds no row (it stays: it costs one query, keeps the
      // fail-closed drop, and covers a registry id outside the curriculum).
      for (const skillId of transferServableSkillIds()) {
        const existing = bySkill.get(skillId);
        if (!existing || existing.length === 0) continue; // never practised — nothing to merge
        let merged = existing;
        if (kidId) {
          // S1(a) — fail AS A WHOLE for this skill, mirroring
          // fetchAllAttemptsForChild's own fail-to-`[]` contract on this
          // SAME call path. Round-1 swallowed a tagged-fetch error to `[]`
          // and quietly kept the bounded window's answer — the exact
          // "regains Mastered by waiting" defect this fetch exists to
          // close, reopened by a transient RLS/network miss with nothing
          // but a console.error. On error, discard this skill's evidence
          // entirely (mastery: null on the tree) rather than asserting
          // mastery from half of it.
          try {
            const transferAttempts = client
              ? await fetchTransferAttemptsForSkillOrThrow(kidId, skillId, 2000, client)
              : await fetchTransferAttemptsForSkillOrThrow(kidId, skillId);
            if (transferAttempts.length > 0) merged = mergeAttempts(merged, transferAttempts);
          } catch (error) {
            console.error(
              `[useSkillGraph] tagged-transfer fetch failed for ${skillId} — discarding this skill's evidence entirely (fail-closed, matching fetchAllAttemptsForChild's own contract):`,
              error,
            );
            merged = [];
          }
        }
        // S1(b) — guest/local-only/offline profiles: merge in the FULL
        // local tagged history (filtered first, never windowed first).
        // Runs regardless of the cloud outcome above and regardless of
        // whether `kidId` exists at all — a local-only child (no `kidId`)
        // gets this as its ONLY source of unbounded tagged coverage.
        const localTagged = loadLocalTaggedTransferAttempts(localChildId, skillId);
        if (localTagged.length > 0) merged = mergeAttempts(merged, localTagged);
        if (merged !== existing) bySkill.set(skillId, merged);
      }
  return { bySkill, cloudRows: cloud, localRows: localAll };
}

/** MA-864 — the tree's row set as one flat list (each skill's rows kept
 *  contiguous and in the tree's order), for a caller that cuts it by time
 *  and folds it again through skillGraphNodesFromAttempts. A skill whose
 *  evidence the tree dropped (fail-closed) has no rows here either. */
export function flattenTreeRows(bySkill: ReadonlyMap<string, readonly ChildAttempt[]>): ChildAttempt[] {
  return [...bySkill.values()].flat();
}

/** Group attempts by skill id (insertion order kept per skill). */
function groupAttemptsBySkill(all: readonly ChildAttempt[]): Map<string, ChildAttempt[]> {
  const bySkill = new Map<string, ChildAttempt[]>();
  for (const a of all) {
    const arr = bySkill.get(a.skillId) ?? [];
    arr.push(a);
    bySkill.set(a.skillId, arr);
  }
  return bySkill;
}

/**
 * MA-775 (RED S1) — the tree's nodes from attempts the CALLER already
 * fetched: the same per-skill mapping buildSkillGraphNodes ends with, with
 * no I/O. The mission pick (useTodaysMission → treeLockedSkillIds) feeds it
 * the merged cloud+local attempts it already read, so it adds no second
 * child_attempts read. Documented divergence from buildSkillGraphNodes: it
 * does not merge the out-of-window tagged-transfer history (MA-303b), which
 * only moves a registry skill between the chain-alive "secure"/"mastered"
 * chips — never into or out of "locked".
 */
export function skillGraphNodesFromAttempts(
  all: readonly ChildAttempt[],
  ownerId: string,
): SkillNode[] {
  return nodesFromGroupedAttempts(groupAttemptsBySkill(all), ownerId);
}

function nodesFromGroupedAttempts(
  bySkill: Map<string, ChildAttempt[]>,
  ownerId: string,
): SkillNode[] {
  return SKILLS.map((s) => {
    const attempts = bySkill.get(s.id);
    return {
      ...s,
      mastery: attempts && attempts.length > 0
        ? toMasteryRecord(ownerId, s.id, attempts)
        : null,
      // MA-297: deriveConfirmedSecure's contract is a CHRONOLOGICAL list
      // (oldest → newest). mergeAttempts already returns ascending order,
      // but sort defensively with the aggregator's own expression
      // (progression.ts aggregateSkillStatesFromAttempts: "Ensure
      // chronological order") so this can never silently depend on a
      // merge-ordering detail. toMasteryRecord above deliberately keeps
      // receiving the untouched `attempts` — zero behavior change there.
      confirmedSecure: attempts && attempts.length > 0
        ? deriveConfirmedSecure(
            attempts.slice().sort((a, b) => a.timestamp.localeCompare(b.timestamp)),
          )
        : false,
    };
  });
}

export function useSkillGraph(
  kidId: string | null | undefined,
  // ENGINE-LOCAL-FIRST: the LOCAL child id (app.activeChildId). Offline the
  // canonical kidId is null and the cloud is empty, so the parent skill tree
  // read "0/N mastered" forever. Merging the local mirror (keyed by this id)
  // lets the tree move off 0 from real offline play. Optional so existing
  // call shapes still compile; when both ids are absent the hook is disabled.
  localChildId?: string | null,
): {
  nodes: SkillNode[];
  loading: boolean;
} {
  const queryClient = useQueryClient();
  const queryKey = ["skill-graph", kidId, localChildId] as const;
  const query = useQuery({
    enabled: !!kidId || !!localChildId,
    queryKey,
    // MA-1202 RED r1 S-1: the ONE shared tier-history read per child.
    queryFn: () =>
      buildSkillGraphNodes(kidId, localChildId, undefined, tierHistoryReader(queryClient, queryKey)),
    staleTime: 30_000,
  });

  return {
    nodes:
      query.data ??
      (kidId || localChildId
        ? []
        : SKILLS.map((s) => ({ ...s, mastery: null, confirmedSecure: false }))),
    loading: query.isLoading,
  };
}
